import { useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { RootState } from '../../store'
import { logout } from '../../store/slices/authSlice'
import { api } from '../../services/api'
import {
  MapPinIcon, BellAlertIcon, MapIcon, UsersIcon,
  HomeIcon, TruckIcon, XMarkIcon, ShieldCheckIcon, ShieldExclamationIcon,
  Cog6ToothIcon, UserGroupIcon, ArrowRightOnRectangleIcon,
  PlayCircleIcon, DocumentArrowUpIcon, BuildingOffice2Icon, SignalIcon, ClockIcon, AcademicCapIcon, Squares2X2Icon, FilmIcon, CpuChipIcon,
  ClipboardDocumentCheckIcon, HomeModernIcon, CalendarDaysIcon, NoSymbolIcon,
} from '@heroicons/react/24/outline'

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
}

interface NavItem {
  to: string
  label: string
  icon: React.ElementType
  roles: string[]
  end?: boolean
}

const mainNav: NavItem[] = [
  { to: '/dashboard',       label: 'nav.dashboard',       icon: HomeIcon,      roles: ['ADMIN', 'MANAGER', 'DISPATCHER'] },
  { to: '/tracking',        label: 'nav.tracking',        icon: MapPinIcon,    roles: ['ADMIN', 'MANAGER', 'DISPATCHER', 'DRIVER'] },
  { to: '/alerts',          label: 'nav.alerts',          icon: BellAlertIcon, roles: ['ADMIN', 'MANAGER', 'DISPATCHER'] },
  { to: '/circuits',        label: 'nav.circuits',        icon: MapIcon,       roles: ['ADMIN', 'MANAGER', 'DISPATCHER'] },
  { to: '/buses',           label: 'nav.buses',           icon: TruckIcon,     roles: ['ADMIN', 'MANAGER', 'DISPATCHER'] },
  { to: '/routes',          label: 'nav.routes',          icon: MapIcon,       roles: ['ADMIN', 'MANAGER', 'DISPATCHER'] },
  { to: '/students',        label: 'nav.students',        icon: UsersIcon,     roles: ['ADMIN', 'MANAGER'] },
  { to: '/driver',          label: 'nav.myRoutes',        icon: TruckIcon,     roles: ['DRIVER'] },
  { to: '/driver/live',     label: 'nav.driverLive',      icon: SignalIcon,    roles: ['DRIVER', 'ADMIN', 'DISPATCHER'] },
  { to: '/driver/inspection', label: 'nav.driverInspection', icon: ClipboardDocumentCheckIcon, roles: ['DRIVER', 'ADMIN', 'DISPATCHER'] },
  { to: '/planning',        label: 'nav.planning',        icon: CalendarDaysIcon, roles: ['ADMIN', 'MANAGER', 'DISPATCHER'] },
  { to: '/trips/assign',    label: 'nav.tripAssign',      icon: CalendarDaysIcon, roles: ['ADMIN', 'MANAGER', 'DISPATCHER'] },
  { to: '/trips',           label: 'nav.tripHistory',     icon: ClockIcon,     roles: ['ADMIN', 'MANAGER', 'DISPATCHER'] },
  { to: '/trips',           label: 'nav.myTrips',         icon: ClockIcon,     roles: ['DRIVER'] },
  { to: '/parent',          label: 'nav.parentHome',      icon: HomeIcon,      roles: ['PARENT'], end: true },
  { to: '/tracking',        label: 'nav.myChildTracking', icon: MapPinIcon,    roles: ['PARENT'] },
  { to: '/parent/schedule', label: 'nav.parentSchedule',  icon: CalendarDaysIcon, roles: ['PARENT'] },
  { to: '/parent/requests', label: 'nav.parentRequests',  icon: ClipboardDocumentCheckIcon, roles: ['PARENT'] },
  { to: '/parent/alerts',   label: 'nav.parentAlerts',    icon: BellAlertIcon, roles: ['PARENT'] },
  { to: '/parent/children', label: 'nav.myChildren',      icon: UsersIcon,     roles: ['PARENT'] },
  { to: '/parent/profile',  label: 'nav.parentProfile',   icon: Cog6ToothIcon, roles: ['PARENT'] },
  { to: '/driver-simulation', label: 'nav.driverSim',     icon: TruckIcon,     roles: ['ADMIN', 'DRIVER', 'DISPATCHER'] },
  { to: '/playback',        label: 'nav.playback',        icon: FilmIcon,      roles: ['ADMIN', 'MANAGER', 'DISPATCHER'] },
  { to: '/demo',            label: 'nav.demo',            icon: PlayCircleIcon, roles: ['ADMIN', 'MANAGER', 'DISPATCHER', 'DRIVER', 'PARENT'] },
]

const adminNav: NavItem[] = [
  { to: '/super-admin',    label: 'nav.tenants',  icon: BuildingOffice2Icon, roles: ['SUPER_ADMIN'], end: true },
  { to: '/admin',          label: 'nav.users',    icon: UserGroupIcon,       roles: ['ADMIN'], end: true },
  { to: '/admin/roles',    label: 'nav.roles',    icon: ShieldCheckIcon,      roles: ['ADMIN'] },
  { to: '/admin/import',   label: 'nav.import',   icon: DocumentArrowUpIcon, roles: ['ADMIN', 'MANAGER'] },
  { to: '/admin/classes',  label: 'nav.classes',  icon: AcademicCapIcon, roles: ['ADMIN', 'MANAGER'] },
  { to: '/admin/zones',    label: 'nav.zones',    icon: ShieldExclamationIcon, roles: ['ADMIN', 'MANAGER'] },
  { to: '/admin/service-zones', label: 'nav.serviceZones', icon: MapIcon, roles: ['ADMIN', 'MANAGER'] },
  { to: '/admin/gps-modules', label: 'nav.gpsModules', icon: CpuChipIcon, roles: ['ADMIN', 'MANAGER'] },
  { to: '/admin/pending-students', label: 'nav.pendingStudents', icon: AcademicCapIcon, roles: ['ADMIN', 'MANAGER'] },
  { to: '/admin/address-requests', label: 'nav.addressRequests', icon: HomeModernIcon, roles: ['ADMIN', 'MANAGER', 'DISPATCHER'] },
  { to: '/admin/absences',  label: 'nav.absences', icon: NoSymbolIcon, roles: ['ADMIN', 'MANAGER', 'DISPATCHER'] },
  { to: '/admin/modules',  label: 'nav.modules',  icon: Squares2X2Icon,       roles: ['ADMIN'] },
  { to: '/admin/settings', label: 'nav.settings', icon: Cog6ToothIcon,        roles: ['ADMIN'] },
]

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { user } = useSelector((s: RootState) => s.auth)
  const { openCount } = useSelector((s: RootState) => s.alerts)

  // Démo désactivable en PROD via le réglage runtime admin `demo.enabled`.
  const [demoEnabled, setDemoEnabled] = useState(true)
  useEffect(() => {
    api.get('/settings')
      .then(r => { setDemoEnabled(r.data?.demo?.enabled !== false) })
      .catch(() => { /* défaut : visible */ })
  }, [])

  const can = (item: NavItem) =>
    (!user?.role || item.roles.includes(user.role)) && (item.to !== '/demo' || demoEnabled)
  const accessibleMain = mainNav.filter(can)
  const accessibleAdmin = adminNav.filter(can)
  const initials = user ? (user.firstName?.[0] ?? '') + (user.lastName?.[0] ?? '') : '?'

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    [
      'group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150',
      isActive ? 'bg-white/15 text-white shadow-sm' : 'text-brand-100/80 hover:bg-white/10 hover:text-white',
    ].join(' ')

  const iconClass = (isActive: boolean) =>
    ['h-4 w-4 flex-shrink-0 transition-colors', isActive ? 'text-white' : 'text-accent-300/80 group-hover:text-accent-200'].join(' ')

  return (
    <aside
      className={[
        'w-64 brand-panel text-white flex flex-col flex-shrink-0',
        'fixed inset-y-0 left-0 z-30 transition-transform duration-200 ease-in-out',
        'lg:relative lg:translate-x-0 lg:z-auto lg:shadow-none shadow-2xl',
        isOpen ? 'translate-x-0' : '-translate-x-full',
      ].join(' ')}
      aria-label="Barre latérale"
    >
      {/* Logo */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex-shrink-0 w-9 h-9 bg-white/15 rounded-xl flex items-center justify-center ring-1 ring-white/10">
            <TruckIcon className="h-5 w-5 text-white" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-extrabold text-white leading-tight">Bus École</p>
            <p className="text-xs text-accent-300 leading-tight"></p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="lg:hidden p-1.5 rounded-lg hover:bg-white/10 text-brand-200 hover:text-white transition-colors"
          aria-label="Fermer le menu"
        >
          <XMarkIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3 px-3" aria-label="Navigation principale">
        <ul className="space-y-0.5" role="list">
          {accessibleMain.map(item => (
            <li key={item.to}>
              <NavLink to={item.to} onClick={onClose} className={linkClass}>
                {({ isActive }) => (
                  <>
                    <item.icon className={iconClass(isActive)} aria-hidden="true" />
                    <span className="flex-1 truncate">{t(item.label)}</span>
                    {item.to === '/alerts' && openCount > 0 && (
                      <span
                        className="flex-shrink-0 min-w-[1.25rem] h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1"
                        aria-label={`${openCount} alertes ouvertes`}
                      >
                        {openCount > 99 ? '99+' : openCount}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>

        {accessibleAdmin.length > 0 && (
          <div className="mt-6">
            <p className="px-3 mb-1.5 text-xs font-semibold uppercase tracking-wider text-accent-300/70">
              {t('nav.section')}
            </p>
            <ul className="space-y-0.5" role="list">
              {accessibleAdmin.map(item => (
                <li key={item.to}>
                  <NavLink to={item.to} end={item.end} onClick={onClose} className={linkClass}>
                    {({ isActive }) => (
                      <>
                        <item.icon className={iconClass(isActive)} aria-hidden="true" />
                        <span className="flex-1 truncate">{t(item.label)}</span>
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        )}
      </nav>

      {/* User footer */}
      {user && (
        <div className="p-3 border-t border-white/10">
          <div className="flex items-center gap-2.5 px-2 py-2 rounded-xl">
            <div
              className="flex-shrink-0 w-7 h-7 bg-accent-500 rounded-full flex items-center justify-center text-xs font-bold text-white uppercase"
              aria-hidden="true"
            >
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-white truncate">{user.firstName} {user.lastName}</p>
              <p className="text-xs text-accent-300 truncate">{t(`roles.${user.role}`, { defaultValue: user.role })}</p>
            </div>
            <button
              onClick={() => dispatch(logout())}
              className="flex-shrink-0 p-1.5 text-brand-200 hover:text-red-300 hover:bg-red-500/15 rounded-lg transition-colors"
              aria-label="Se déconnecter"
              title="Se déconnecter"
            >
              <ArrowRightOnRectangleIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
    </aside>
  )
}
